import { formatHumanName } from "@medplum/core";
import type { Annotation as FhirAnnotation, HumanName, Task } from "@medplum/fhirtypes";
import { getTRPCClient, PRACTITIONER_ENTITY_ID_SYSTEM, TASK_ENTITY_ID_SYSTEM } from "@repo/api";
import {
  type CreateTaskInput,
  createTaskInputSchema,
  practitionerIdSchema,
  type SetTaskFieldInput,
  setTaskFieldInputSchema,
  taskIdSchema,
} from "@repo/api/schemas";
import type { PractitionerId, TaskId } from "@repo/domain";
import { Patient } from "@repo/domain";
import { RETRACTION_SYSTEM } from "@repo/fhir-systems/code-systems";
import { withMentions } from "@repo/mentions";
import { coalesce } from "@tanstack/db";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { useMemo } from "react";
import { defineWrite } from "../actions/define-write";
import { useCollection } from "../core/hooks";
import { getDataStoreClient } from "../core/provider";
import { DomainResultError } from "../core/write-errors";
import type { PractitionerRow, TaskRow } from "../fhir/types";
import { optimisticPatientReference } from "./_patient-reference";
import { optimisticPractitionerReference, practitionerDomainId } from "./_practitioner-reference";

const TASK_COLLECTION = "task";
const PRACTITIONER_COLLECTION = "practitioner";
const PATIENT_COLLECTION = "patient";

/** Gap between consecutive sort-order values when inserting tasks (gap list pattern). */
export const TASK_SORT_ORDER_GAP = 10_000;

/**
 * Compute a gap-based decimal sort order for inserting a task between two neighbours.
 *
 * - Both prev and next provided → exact midpoint (AC3).
 * - Only next provided (first position) → next − gap (AC4).
 * - Only prev provided (last/append) → prev + gap (AC5).
 * - Neither provided → gap (first task in an empty list).
 *
 * Float-precision note: IEEE 754 doubles exhaust significand bits after roughly
 * 52 successive halvings within a fixed gap (e.g. 10 000).  At that depth
 * `(prev + next) / 2 === prev`, producing a silent collision.  The remedy is a
 * rebalance pass that redistributes all sort orders with fresh multiples of the
 * gap constant.  No guard is added here; callers should detect the degenerate
 * case and trigger a rebalance when needed.
 */
export function computeGapSortOrder(prev?: number, next?: number, gap = TASK_SORT_ORDER_GAP): number {
  if (prev !== undefined && next !== undefined) return (prev + next) / 2;
  if (prev !== undefined) return prev + gap;
  if (next !== undefined) return next - gap;
  return gap;
}

interface UseTasksParams {
  taskId?: TaskId;
}

interface UseTasksResult {
  tasks: TaskDto[];
  isLoading: boolean;
  isReady: boolean;
  isError: boolean;
}

/** Manual tasks carry taskId; work-item marker rows deliberately do not. */
export type TaskDto = Task & { taskId?: TaskId; assigneeId?: PractitionerId };
export type ManualTaskDto = TaskDto & { taskId: TaskId };

export function isManualTask(task: TaskDto): task is ManualTaskDto {
  return task.taskId !== undefined;
}

/**
 * Live query for tasks with practitioner-display enrichment of `requester` and
 * `note.authorReference`. Returns raw FHIR `Task` so detail views can reach
 * extensions and meta. New callers should treat this as the single read entry
 * point for the task collection.
 */
export function useTasks(params?: UseTasksParams): UseTasksResult {
  const taskCollection = useCollection(TASK_COLLECTION);
  const practitionerCollection = useCollection(PRACTITIONER_COLLECTION);
  const patientCollection = useCollection(PATIENT_COLLECTION);
  const taskId = params?.taskId;

  const joinResult = useLiveQuery({
    query: (q) => {
      let tasks = q.from({ t: taskCollection });
      if (taskId) tasks = tasks.where(({ t }) => eq(t.domainId, taskId));

      // Patient names live on the Patient resource directly (ADR-0006).
      //
      // `requester` and `for` are both optional. The identity is coalesced to a
      // sentinel, so an absent one joins nothing rather than every person whose
      // own identity is also absent. Pinned by "a task with no requester and no
      // subject" in live-query-semantics.
      return tasks
        .leftJoin({ pr: practitionerCollection }, ({ t, pr }) =>
          eq(coalesce(t.requester?.identifier?.value, "__no_requester__"), pr.domainId),
        )
        .leftJoin({ pat: patientCollection }, ({ t, pat }) =>
          eq(coalesce(t.for?.identifier?.value, "__no_subject__"), pat.domainId),
        )
        .select(({ t, pr, pat }) => ({
          t: { ...t },
          requesterName: pr?.name,
          forName: pat?.name,
        }));
    },
  });

  // Sibling lookup for note.authorReference enrichment — notes are an array
  // inside each task, so the join builder can't reach them.
  const practitionerResult = useLiveQuery({
    query: (q) => q.from({ p: practitionerCollection }).select(({ p }) => ({ ...p })),
  });

  const tasks = useMemo(() => {
    const rows =
      (joinResult.data as Array<{ t: TaskRow; requesterName?: HumanName[]; forName?: HumanName[] }> | undefined) ?? [];
    const practitioners = (practitionerResult.data as PractitionerRow[] | undefined) ?? [];
    const practitionerMap = new Map<string, string | undefined>(
      practitioners.flatMap((practitioner) =>
        practitioner.id
          ? [[practitioner.id, practitioner.name?.[0] ? formatHumanName(practitioner.name[0]) : undefined] as const]
          : [],
      ),
    );

    return rows.map(({ t, requesterName, forName }) => {
      const assigneeId = practitionerDomainId(t.owner);
      let enriched: TaskDto = {
        ...t,
        ...(t.domainId ? { taskId: taskIdSchema.parse(t.domainId) } : {}),
        ...(assigneeId ? { assigneeId: practitionerIdSchema.parse(assigneeId) } : {}),
      };
      if (requesterName?.[0] && t.requester && !t.requester.display) {
        const display = formatHumanName(requesterName[0]);
        if (display) enriched = { ...enriched, requester: { ...enriched.requester, display } };
      }
      const patientName = Patient.displayName(forName);
      if (patientName && t.for && !t.for.display) {
        const display = formatHumanName(patientName);
        if (display) enriched = { ...enriched, for: { ...enriched.for, display } };
      }
      if (t.note?.length) {
        const enrichedNotes = t.note.map((n) => {
          if (n.authorReference?.display || !n.authorReference?.reference) return n;
          const [, id] = n.authorReference.reference.split("/");
          const display = id ? practitionerMap.get(id) : undefined;
          if (!display) return n;
          return { ...n, authorReference: { ...n.authorReference, display } };
        });
        enriched = { ...enriched, note: enrichedNotes };
      }
      return enriched;
    });
  }, [joinResult.data, practitionerResult.data]);

  return {
    tasks,
    isLoading: joinResult.isLoading,
    isReady: joinResult.isReady,
    isError: joinResult.isError,
  };
}

/**
 * Refetch the Task collection from the server.
 *
 * For callers without subscription sync-back: a Task whose status changed as a
 * side effect of another intent (work-sync closing a work item when its focus
 * resource completes) is invisible to the live queries until the collection is
 * refetched. The patient portal is the case in point — a patient cannot write
 * `Subscription`, so `FhirDataStoreProvider` gates subscriptions off there and
 * every status a work item derives from elsewhere reads stale until reload.
 */
export function useRefreshTasks(): () => Promise<void> {
  const collection = useCollection(TASK_COLLECTION);
  return () => collection.utils.refetch();
}

// ============================================================================
// Mutations — module-level intent actions.
// ============================================================================

const tasks = () => getDataStoreClient().getCollection<TaskRow, string>(TASK_COLLECTION);

/** Rows are keyed by domain identity; this only asserts the row is loaded. */
function resourceIdForTaskId(taskId: TaskId): string {
  if (!tasks().has(taskId)) throw new Error(`Task '${taskId}' is not loaded`);
  return taskId;
}

/** Intent: "draft a new task". */
export const draftTask = defineWrite<CreateTaskInput>({
  optimistic: (params) => {
    const input = createTaskInputSchema.parse(params);
    const optimisticTaskId = input.id;
    tasks().insert({
      resourceType: "Task",
      identifier: [{ system: TASK_ENTITY_ID_SYSTEM, value: optimisticTaskId }],
      domainId: optimisticTaskId,
      ...(input.patient ? { patientId: input.patient.id } : {}),
      status: input.status ?? "draft",
      intent: "order",
      description: input.description,
      ...(input.priority ? { priority: input.priority } : {}),
      ...(input.patient && { for: optimisticPatientReference(input.patient.id) }),
      ...(input.owner && {
        owner:
          input.owner.resourceType === "Practitioner"
            ? optimisticPractitionerReference(input.owner.id)
            : { reference: `${input.owner.resourceType}/${input.owner.id}` },
      }),
      ...(input.dueDate && { restriction: { period: { end: input.dueDate } } }),
    });
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().task.draftTask.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/** Intent: "patch task fields (priority/dueDate/description)". */
export const setTaskField = defineWrite<SetTaskFieldInput>({
  optimistic: (input) => {
    tasks().update(resourceIdForTaskId(input.taskId), (draft) => {
      if (input.priority !== undefined) draft.priority = input.priority;
      if (input.description !== undefined) draft.description = input.description;
      if (input.dueDate !== undefined) {
        draft.restriction =
          input.dueDate === null
            ? undefined
            : { ...draft.restriction, period: { ...draft.restriction?.period, end: input.dueDate } };
      }
    });
  },
  persist: async ({ taskId, priority, dueDate, description }, options) => {
    const apiInput = setTaskFieldInputSchema.parse({
      taskId,
      ...(priority !== undefined ? { priority } : {}),
      ...(dueDate !== undefined ? { dueDate } : {}),
      ...(description !== undefined ? { description } : {}),
    });
    const result = await getTRPCClient().task.setTaskField.mutate(apiInput, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/**
 * Intent: "change a task's status to <X>". Domain dispatches by target status
 * and applies per-transition side effects (executionPeriod start/end, cancel
 * reason). Draft/requested are creation-only and not accepted as targets.
 *
 */
export type SettableTaskStatus = "ready" | "in-progress" | "on-hold" | "completed" | "cancelled" | "failed";

export const setTaskStatus = defineWrite<{ taskId: TaskId; status: SettableTaskStatus }>({
  optimistic: ({ taskId, status }) => {
    const collection = tasks();
    const resourceId = resourceIdForTaskId(taskId);
    collection.update(resourceId, (draft) => {
      draft.status = status;
      if (["completed", "cancelled", "failed"].includes(status)) {
        draft.executionPeriod = { ...draft.executionPeriod, end: new Date().toISOString() };
        if (status !== "completed") {
          const display = status === "failed" ? "Failed" : "Cancelled";
          draft.statusReason = { coding: [{ system: RETRACTION_SYSTEM, code: status, display }], text: display };
        }
      }
    });
  },
  persist: async ({ taskId, status }, options) => {
    const result = await getTRPCClient().task.setStatus.mutate({ taskId, status }, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/** Intent: "cancel a procedure task". */
export const cancelProcedure = ({ taskId }: { taskId: TaskId }) => setTaskStatus({ taskId, status: "cancelled" });

/** Intent: "assign a task to an owner". */
export const assignTask = defineWrite<{
  taskId: TaskId;
  owner: { resourceType: "Practitioner"; id: PractitionerId; display?: string };
}>({
  optimistic: ({ taskId, owner }) => {
    const collection = tasks();
    const resourceId = resourceIdForTaskId(taskId);
    collection.update(resourceId, (draft) => {
      draft.owner = {
        identifier: { system: PRACTITIONER_ENTITY_ID_SYSTEM, value: owner.id },
        ...(owner.display && { display: owner.display }),
      };
    });
  },
  persist: async ({ taskId, owner }, options) => {
    const result = await getTRPCClient().task.assign.mutate({ taskId, owner }, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/**
 * Intent: "add a comment (annotation) to a task".
 *
 */
export const addTaskComment = defineWrite<{
  taskId: TaskId;
  text: string;
  authorDisplay?: string;
  authorId?: PractitionerId;
  recipients?: Array<{ reference: string; display?: string }>;
}>({
  optimistic: ({ taskId, text, authorDisplay, authorId, recipients }) => {
    let annotation: FhirAnnotation = { time: new Date().toISOString(), text };
    if (authorId) {
      annotation.authorReference = {
        identifier: { system: PRACTITIONER_ENTITY_ID_SYSTEM, value: authorId },
        ...(authorDisplay && { display: authorDisplay }),
      };
    } else if (authorDisplay) {
      annotation.authorString = authorDisplay;
    }
    annotation = withMentions(annotation, recipients ?? []);

    const collection = tasks();
    const resourceId = resourceIdForTaskId(taskId);
    const existing = collection.get(resourceId);
    if (!existing) return;
    const nextNote = [...(existing.note ?? []), annotation];
    collection.update(resourceId, (draft) => {
      draft.note = nextNote;
    });
  },
  persist: async ({ taskId, text, recipients }, options) => {
    const result = await getTRPCClient().task.addComment.mutate(
      {
        taskId,
        text,
        recipients,
      },
      options,
    );
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});
